import { useState } from "react";
import { UseFormRegister, FieldErrors, UseFormWatch, UseFormSetValue } from "react-hook-form";
import { Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { CreateTripInput } from "@/lib/validations";

interface RouteFormProps {
  index: number;
  onRemove: () => void;
  canRemove: boolean;
  register: UseFormRegister<CreateTripInput>;
  errors: FieldErrors<CreateTripInput>;
  watch: UseFormWatch<CreateTripInput>;
  setValue: UseFormSetValue<CreateTripInput>;
  legLabel?: string;
  onDestinationChange?: (destination: string) => void;
}

export default function RouteForm({ index, onRemove, canRemove, register, errors, watch, setValue, legLabel, onDestinationChange }: RouteFormProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const timeType = watch("routes." + index + ".timeConstraint.type" as any) || "any";
  const routeErrors = errors.routes?.[index];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <span className="font-medium text-sm">{legLabel || `Route ${index + 1}`}</span>
          {canRemove && (
            <Button type="button" variant="ghost" size="sm" onClick={onRemove} className="text-destructive hover:text-destructive">
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Origin Airport</Label>
            <Input
              placeholder="DFW"
              maxLength={3}
              {...register("routes." + index + ".origin" as any, {
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  e.target.value = e.target.value.toUpperCase();
                },
              })}
            />
            {routeErrors?.origin && (
              <p className="text-destructive text-xs">{routeErrors.origin.message as string}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Destination Airport</Label>
            <Input
              placeholder="MIA"
              maxLength={3}
              {...register("routes." + index + ".destination" as any, {
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  e.target.value = e.target.value.toUpperCase();
                  onDestinationChange?.(e.target.value);
                },
              })}
            />
            {routeErrors?.destination && (
              <p className="text-destructive text-xs">{routeErrors.destination.message as string}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Departure Date</Label>
            <Input 
              type="date"
              min={new Date().toISOString().split("T")[0]} 
              {...register("routes." + index + ".departureDate" as any)} 
            />
            {routeErrors?.departureDate && (
              <p className="text-destructive text-xs">{routeErrors.departureDate.message as string}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Time Preference</Label>
            <Select
              value={timeType}
              onValueChange={(val) =>
                setValue("routes." + index + ".timeConstraint.type" as any, val)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Any Time" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any Time</SelectItem>
                <SelectItem value="after">Depart After</SelectItem>
                <SelectItem value="before">Arrive Before</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {timeType !== "any" && (
            <div className="space-y-2">
              <Label>{timeType === "after" ? "Depart After" : "Arrive Before"}</Label>
              <Input type="time" {...register("routes." + index + ".timeConstraint.time" as any)} />
            </div>
          )}
        </div>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="text-muted-foreground"
        >
          {showAdvanced ? <ChevronUp className="h-4 w-4 mr-1" /> : <ChevronDown className="h-4 w-4 mr-1" />}
          Advanced Options
        </Button>

        {showAdvanced && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-border">
            <div className="space-y-2">
              <Label>Earliest Departure</Label>
              <Input type="time" {...register("routes." + index + ".earliest_departure" as any)} />
            </div>
            <div className="space-y-2">
              <Label>Latest Arrival</Label>
              <Input type="time" {...register("routes." + index + ".latest_arrival" as any)} />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id={"direct-" + index}
                className="rounded border-border"
                {...register("routes." + index + ".direct_flights_only" as any)}
              />
              <Label htmlFor={"direct-" + index}>Direct flights only</Label>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
